import { useCallback, useMemo, useState } from 'react';

/**
 * Tracks whether the current form state differs from its baseline.
 *
 * The baseline is captured from the FIRST RENDER's `initial` — including `null`
 * if that is what the first render passed. (An earlier version of this comment
 * claimed it captured "the first non-null initial"; `useState(initial)` does not
 * do that, and the claim misled a consumer into writing its own copy.)
 *
 * `resetBaseline` re-anchors it, which a page that SAVES WITHOUT NAVIGATING
 * needs: without it the SaveBar reports unsaved changes forever and the unload
 * warning stays armed for the rest of the session (#468). The baseline is held
 * in state (not a ref) precisely so that `resetBaseline` alone forces the
 * re-render `dirty` depends on — a caller that calls it and does nothing else
 * (no other state change of its own) must still see `dirty` flip on its next
 * read. A ref-plus-counter would need the counter kept as a memo dependency
 * purely to fake that, with nothing in the type system or a `current` that
 * changes identity on every render to catch its removal.
 *
 * Comparison is a JSON serialization, so values must be JSON-safe.
 */
export function useFormDirty<T>(initial: T, current: T): {
  dirty: boolean;
  resetBaseline: (next: T) => void;
} {
  const [baseline, setBaseline] = useState<T>(initial);

  const resetBaseline = useCallback((next: T) => {
    setBaseline(next);
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(baseline) !== JSON.stringify(current),
    [baseline, current],
  );

  return { dirty, resetBaseline };
}
